import { Family } from "protogen/advisors_service_pb";
import { Box } from "@mui/material";
import { SectionProps } from "../details-page/Section";
import { EventNotice, EventNotice_NoticeType } from "protogen/calendar_pb";
import FayeCalendar from "components/calendar/FayeCalendar";
import {
  useAdvisorCalendarSearch,
  useListEvents,
  useRefreshCalendars,
} from "services/calendar";
import {
  AdvisorCalendarSearchRequest,
  ListEventsRequest,
} from "protogen/calendar_service_pb";
import { useEffect, useState, useContext } from "react";
import { CurrentUserContext } from "../context/RequireAuth";
import { defaultParametersV2, CalendarParametersV2 } from "types/calendars";
import { CalendarFilter } from "../calendar/CustomCalendarSettings";
import { createColorMap, defaultColor } from "../calendar/utils";
import useDateParams from "components/hooks/calendar/useDateParams";
import { deduplicate, getWeekBoundaryInfo } from "components/calendar/utils";
import { useTimezoneMismatch } from "../hooks/useTimezoneMismatch";
import TimezoneMismatchBanner from "../calendar/TimezoneMismatchBanner";

const FAYE_CALENDAR = "_faye-events";

interface Props extends SectionProps {
  family: Family;
  refreshTrigger: number;
}
export default ({ family, refreshTrigger }: Props) => {
  const defaultDate = useDateParams();
  const currentUser = useContext(CurrentUserContext);
  const { showMismatch, browserTimezone, userTimezone } = useTimezoneMismatch();
  // For family calendar: primary timezone is advisor's, secondary is family's if different
  const advisorTimezone =
    currentUser?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const familyTimezone = family.familyMembers?.[0]?.timezone;

  const [viewTimezone] = useState<string>(advisorTimezone);
  const [secondaryTimezone] = useState<string>(
    familyTimezone && familyTimezone !== advisorTimezone ? familyTimezone : "",
  );
  const [dualTimezoneEnabled] = useState<boolean>(
    !!(familyTimezone && familyTimezone !== advisorTimezone),
  );

  const [events, setEvents] = useState<EventNotice[]>([]);
  const [params, setParams] = useState<CalendarParametersV2>({
    ...defaultParametersV2(),
  });
  const [enabledCalendars, setEnabledCalendars] = useState<
    CalendarFilter[] | undefined
  >(undefined);
  const { request: refreshRequest } = useRefreshCalendars();
  const { request: advisorCalendarSearchRequest } = useAdvisorCalendarSearch();
  const [calendarColorMap, setCalendarColorMap] = useState<Record<
    string,
    [string, string]
  > | null>(null);
  const { request, loading, data } = useListEvents((r) => {
    if (!calendarColorMap) {
      setCalendarColorMap(
        createColorMap(
          (r?.calendars || []).map(
            (c) => `${c.integrationRef}|${c.calendarId}`,
          ),
        ),
      );
    }
  });
  const fetchEvents = async (
    newDate: Date,
    includeDuplicates: boolean = false,
  ) => {
    const { currentMonth, currentYear, otherMonth, otherYear } =
      getWeekBoundaryInfo(newDate);

    setParams({
      ...params,
      month: currentMonth,
      year: currentYear,
      day: newDate.getDate(),
    });

    const requests = [
      request(
        new ListEventsRequest({
          ...params,
          month: currentMonth,
          year: currentYear,
          includeExternalEvents: true,
          includeAdvisorEvents: false,
          familyRefs: [family.ref],
          includeDuplicateEvents: includeDuplicates,
        }),
      ),
    ];

    if (otherMonth !== null && otherYear !== null) {
      requests.push(
        request(
          new ListEventsRequest({
            ...params,
            familyRefs: [family.ref],
            month: otherMonth,
            year: otherYear,
            includeExternalEvents: true,
            includeAdvisorEvents: false,
            includeOverdueTasks: false,
            includeDuplicateEvents: includeDuplicates,
          }),
        ),
      );
    }

    const results = await Promise.all(requests);
    // A basic dedupe on eventRef and startSec.  The FE requests month/year,
    // but the backend request an extra day window on each end to account for
    // timezone.  A more robust API that accepts more granular time or other improvements
    // can remove this dedupe.
    const allEvents = deduplicate(
      // @ts-ignore
      results.flatMap((result) => result?.eventNotices || []),
      ["eventRef", "startSec"],
    );
    setEvents(allEvents);
  };
  useEffect(() => {
    fetchEvents(defaultDate);
  }, []);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchEvents(new Date(params.year, params.month - 1, params.day));
    }
  }, [refreshTrigger]);
  return (
    <>
      <TimezoneMismatchBanner
        show={showMismatch}
        browserTimezone={browserTimezone}
        userTimezone={userTimezone}
        dismissible={false}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          alignSelf: "stretch",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <FayeCalendar
          accountType="advisor"
          familyRef={family.ref}
          events={events.filter((e) => {
            if (!enabledCalendars) {
              return true;
            }
            const calSet = new Set(enabledCalendars?.map((c) => c.key) || []);
            if (
              e.noticeType === EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT
            ) {
              return calSet.has(
                `${e.externalCalendarRef}|${e.externalCalendarId}`,
              );
            }
            return calSet.has(FAYE_CALENDAR);
          })}
          getEventStyles={(event) => {
            const colors =
              calendarColorMap?.[
                `${event.externalCalendarRef}|${event.externalCalendarId}`
              ];
            return {
              backgroundColor: colors?.[0] || defaultColor[0],
              color: colors?.[1] || defaultColor[1],
            };
          }}
          calendarFilters={[
            ...(data?.calendars || [])
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => ({
                key: `${c.integrationRef}|${c.calendarId}`,
                name: c.name,
                color:
                  calendarColorMap?.[`${c.integrationRef}|${c.calendarId}`][0],
              })),
            {
              name: "Faye Events",
              key: FAYE_CALENDAR,
            },
          ]}
          setEnabledFilters={(filters) => {
            setEnabledCalendars(filters);
          }}
          embeddedMode={true}
          refreshEvents={async (includeDuplicates) => {
            await fetchEvents(
              new Date(params.year, params.month - 1, params.day),
              includeDuplicates,
            );
          }}
          refreshCalendar={async () => {
            await refreshRequest({
              familyRef: family.ref,
            });
          }}
          handleNavigate={async (d) => {
            fetchEvents(d);
          }}
          loading={loading}
          headerViewType="compact"
          viewTimezone={viewTimezone}
          onTimezoneChange={undefined}
          secondaryTimezone={secondaryTimezone}
          dualTimezoneEnabled={dualTimezoneEnabled}
          onDualTimezoneChange={undefined}
          families={[family]}
          searchEvents={async (query) => {
            const response = await advisorCalendarSearchRequest(
              new AdvisorCalendarSearchRequest({
                query,
                familyRefs: [family.ref],
                includeAdvisorEvents: false,
                limit: 100,
              }),
            );
            const results = response?.eventNotices || [];
            if (!enabledCalendars) {
              return results;
            }
            const calSet = new Set(
              enabledCalendars.map((calendar) => calendar.key),
            );
            return results.filter((event) => {
              if (
                event.noticeType ===
                EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT
              ) {
                return calSet.has(
                  `${event.externalCalendarRef}|${event.externalCalendarId}`,
                );
              }
              return calSet.has(FAYE_CALENDAR);
            });
          }}
        />
      </Box>
    </>
  );
};
